import { DeleteOutlined, WarningOutlined } from '@ant-design/icons';
import { useApolloClient } from '@apollo/client';
import { Button, message, Popconfirm } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../../shared/permissions';
import { type UserGroupSpecsFragment } from '../../../api/fragments/UserGroupSpecs';
import {
    RemoveUserOnUserGroupDocument,
    type RemoveUserOnUserGroupMutation,
    type RemoveUserOnUserGroupMutationVariables,
} from '../../../api/mutations/removeUserOnUserGroup';
import hasPermissions from '../../../utilities/hasPermissions';

export type UserGroupUserListActionsProps = {
    userGroup: UserGroupSpecsFragment;
    user: UserGroupSpecsFragment['users'][number];
};

const UserGroupUserListActions = ({ user, userGroup }: UserGroupUserListActionsProps) => {
    const { t } = useTranslation(['userGroupDetails']);

    const apolloClient = useApolloClient();
    const userId = user.id;
    const userGroupId = userGroup.id;

    const onDelete = useCallback(async () => {
        message.loading({
            content: t('userGroupDetails:deleteUser.messages.submitting'),
            key: 'primary',
            duration: 0,
        });

        await apolloClient
            .mutate<RemoveUserOnUserGroupMutation, RemoveUserOnUserGroupMutationVariables>({
                mutation: RemoveUserOnUserGroupDocument,
                variables: { userGroupId, userId },
            })
            .finally(() => {
                message.destroy('primary');
            });

        message.success({
            content: t('userGroupDetails:deleteUser.messages.success'),
            key: 'primary',
        });
    }, [t, apolloClient, userId, userGroupId]);

    if (!hasPermissions(userGroup.userGroupPermissions, [permissionKind.updateUserGroup])) {
        return null;
    }

    return (
        <Popconfirm
            cancelText={t('userGroupDetails:deleteUser.cancelText')}
            icon={<WarningOutlined style={{ color: 'red' }} />}
            okText={t('userGroupDetails:deleteUser.okText')}
            onConfirm={onDelete}
            placement="left"
            title={t('userGroupDetails:deleteUser.message')}
        >
            <Button icon={<DeleteOutlined />} type="link" />
        </Popconfirm>
    );
};

export default UserGroupUserListActions;
